/**
 * Announcement Form Component
 * 
 * Modal form for creating and editing announcements with target audience filtering
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Megaphone,
  Upload,
  X,
  Save,
  Users,
  Calendar,
  Link as LinkIcon,
  FileText,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { Announcement, AnnouncementFormData, AnnouncementPriority } from '../../@types/announcement';
import { AnnouncementService } from '../../services/AnnouncementService';
import { userService } from '../../services/UserService';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { AnnouncementPriorityBadge } from './AnnouncementPriorityBadge';

interface AnnouncementFormProps {
  announcement?: Announcement;
  mode: 'create' | 'edit';
  isLoading?: boolean;
  onSubmit: (data: AnnouncementFormData) => Promise<void>;
  onCancel: () => void;
}

const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${Spacing.lg};
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const FormContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.xxl};
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.xl};
  border-bottom: 1px solid ${ThemeColors.border.light};
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const FormTitle = styled.h2`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};

  svg {
    color: ${ThemeColors.primary.main};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.sm};
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const FormContent = styled.div`
  padding: ${Spacing.xl};
`;

const FormGroup = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};

  svg {
    width: 16px;
    height: 16px;
    color: ${ThemeColors.primary.main};
  }
`;

const Required = styled.span`
  color: ${ThemeColors.error.main};
  margin-left: ${Spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${Spacing.md};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${Spacing.md};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-family: inherit;
  resize: vertical;
  min-height: 150px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${Spacing.md};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
  padding: ${Spacing.md};
  background: ${ThemeColors.background.secondary};
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  cursor: pointer;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: ${ThemeColors.primary.main};
  }
`;

const TagInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  min-height: 44px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.dark};
  border-radius: 16px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};

  button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${ThemeColors.primary.dark};
    display: flex;
    align-items: center;

    &:hover {
      color: ${ThemeColors.error.main};
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const TagInputField = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 100px;
  font-size: ${Typography.fontSize.sm};
  padding: ${Spacing.xs};

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const FileUploadArea = styled.div<{ isDragging?: boolean }>`
  border: 2px dashed ${props => props.isDragging ? ThemeColors.primary.main : ThemeColors.border.main};
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.xxl};
  text-align: center;
  background: ${props => props.isDragging ? ThemeColors.primary.light : ThemeColors.background.secondary};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
  }
`;

const UploadIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto ${Spacing.md};
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 30px;
    height: 30px;
  }
`;

const UploadText = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.primary};
  font-weight: ${Typography.fontWeight.medium};
  margin: 0 0 ${Spacing.xs};
`;

const UploadSubtext = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${ThemeColors.background.secondary};
  border-radius: ${BorderRadius.sm};
  border: 1px solid ${ThemeColors.border.light};
  margin-top: ${Spacing.md};
`;

const FileName = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.xs};
  cursor: pointer;
  color: ${ThemeColors.error.main};
  border-radius: ${BorderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${ThemeColors.error.light};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${Spacing.md};
  padding: ${Spacing.xl};
  border-top: 1px solid ${ThemeColors.border.light};
  position: sticky;
  bottom: 0;
  background: white;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md} ${Spacing.xl};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
    color: white;
    box-shadow: ${Shadows.md};
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: ${Shadows.lg};
    }
    
    &:disabled {
      background: ${ThemeColors.gray[400]};
      cursor: not-allowed;
      transform: none;
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 2px solid ${ThemeColors.border.main};
    
    &:hover {
      background: ${ThemeColors.background.secondary};
      border-color: ${ThemeColors.primary.main};
    }
  `}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  color: ${ThemeColors.error.main};
  font-size: ${Typography.fontSize.sm};
  margin-top: ${Spacing.xs};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const InfoText = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin-top: ${Spacing.xs};
`;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export const AnnouncementForm: React.FC<AnnouncementFormProps> = ({
  announcement,
  mode,
  isLoading = false,
  onSubmit,
  onCancel
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Form state
  const [title, setTitle] = useState(announcement?.title || '');
  const [content, setContent] = useState(announcement?.content || '');
  const [priority, setPriority] = useState<AnnouncementPriority>(
    announcement?.priority || 'MEDIUM'
  );
  const [detailsLink, setDetailsLink] = useState(announcement?.detailsLink || '');
  const [expiresAt, setExpiresAt] = useState<string>(
    announcement?.expiresAt 
      ? new Date(announcement.expiresAt.toDate()).toISOString().split('T')[0]
      : ''
  );
  const [attachment, setAttachment] = useState<File | null>(null);
  const [existingAttachment, setExistingAttachment] = useState<{
    url: string;
    name: string;
    type: string;
  } | null>(
    announcement?.attachmentUrl 
      ? {
          url: announcement.attachmentUrl,
          name: announcement.attachmentName || 'attachment',
          type: announcement.attachmentType || 'application/octet-stream'
        }
      : null
  );

  // Target audience state
  const [allUsers, setAllUsers] = useState(announcement?.targetAudience.allUsers || false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    announcement?.targetAudience.roles || []
  );
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    announcement?.targetAudience.userIds || []
  );
  const [selectedSalesGroups, setSelectedSalesGroups] = useState<string[]>(
    announcement?.targetAudience.salesGroups || []
  );
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>(
    announcement?.targetAudience.provinces || []
  );

  // User selection state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [salesGroupInput, setSalesGroupInput] = useState('');
  const [provinceInput, setProvinceInput] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load users for selection
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await userService.getAllUsers();
        setAvailableUsers(users);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachment(file);
      setExistingAttachment(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setAttachment(file);
      setExistingAttachment(null);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setExistingAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addUser = (userId: string) => {
    if (!selectedUserIds.includes(userId)) {
      setSelectedUserIds([...selectedUserIds, userId]);
      setUserSearchTerm('');
    }
  };

  const removeUser = (userId: string) => {
    setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
  };

  const addSalesGroup = () => {
    if (salesGroupInput && !selectedSalesGroups.includes(salesGroupInput)) {
      setSelectedSalesGroups([...selectedSalesGroups, salesGroupInput]);
      setSalesGroupInput('');
    }
  };

  const removeSalesGroup = (group: string) => {
    setSelectedSalesGroups(selectedSalesGroups.filter(g => g !== group));
  };

  const addProvince = () => {
    if (provinceInput && !selectedProvinces.includes(provinceInput)) {
      setSelectedProvinces([...selectedProvinces, provinceInput]);
      setProvinceInput('');
    }
  };

  const removeProvince = (province: string) => {
    setSelectedProvinces(selectedProvinces.filter(p => p !== province));
  };

  const toggleRole = (role: string) => {
    setSelectedRoles(
      selectedRoles.includes(role)
        ? selectedRoles.filter(r => r !== role)
        : [...selectedRoles, role]
    );
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!content.trim()) {
      newErrors.content = 'Content is required';
    }

    if (!allUsers && selectedRoles.length === 0 && selectedUserIds.length === 0 && 
        selectedSalesGroups.length === 0 && selectedProvinces.length === 0) {
      newErrors.targetAudience = 'Please select at least one target audience option';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const formData: AnnouncementFormData = {
      title: title.trim(),
      content: content.trim(),
      priority,
      detailsLink: detailsLink.trim() || undefined,
      expiresAt: expiresAt ? new Date(expiresAt) : undefined,
      attachment,
      targetAudience: {
        allUsers,
        roles: allUsers ? undefined : selectedRoles.length > 0 ? selectedRoles : undefined,
        userIds: allUsers ? undefined : selectedUserIds.length > 0 ? selectedUserIds : undefined,
        salesGroups: allUsers ? undefined : selectedSalesGroups.length > 0 ? selectedSalesGroups : undefined,
        provinces: allUsers ? undefined : selectedProvinces.length > 0 ? selectedProvinces : undefined
      }
    };

    await onSubmit(formData);
  };

  const filteredUsers = availableUsers.filter(user =>
    (user.username || user.email || '').toLowerCase().includes(userSearchTerm.toLowerCase()) &&
    !selectedUserIds.includes(user.id)
  );

  return (
    <FormOverlay onClick={onCancel}>
      <FormContainer onClick={(e) => e.stopPropagation()}>
        <FormHeader>
          <FormTitle>
            <Megaphone />
            {mode === 'create' ? 'Create Announcement' : 'Edit Announcement'}
          </FormTitle>
          <CloseButton onClick={onCancel}>
            <X />
          </CloseButton>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormContent>
            <FormGroup>
              <Label>
                <Megaphone />
                Title <Required>*</Required>
              </Label>
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter announcement title"
                required
              />
              {errors.title && (
                <ErrorMessage>
                  <AlertCircle />
                  {errors.title}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>
                <FileText />
                Content <Required>*</Required>
              </Label>
              <TextArea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter announcement content..."
                required
              />
              {errors.content && (
                <ErrorMessage>
                  <AlertCircle />
                  {errors.content}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGrid>
              <FormGroup>
                <Label>
                  Priority
                </Label>
                <Select value={priority} onChange={(e) => setPriority(e.target.value as AnnouncementPriority)}>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </Select>
                <InfoText>
                  <AnnouncementPriorityBadge priority={priority} size="sm" />
                </InfoText>
              </FormGroup>

              <FormGroup>
                <Label>
                  <Calendar />
                  Expiry Date (Optional)
                </Label>
                <Input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
                <InfoText>Announcement will expire after this date</InfoText>
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <Label>
                <LinkIcon />
                Details Link (Optional)
              </Label>
              <Input
                type="url"
                value={detailsLink}
                onChange={(e) => setDetailsLink(e.target.value)}
                placeholder="https://example.com/details"
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Upload />
                Attachment (Optional)
              </Label>
              {attachment || existingAttachment ? (
                <FileItem>
                  <FileName>
                    <FileText />
                    {attachment?.name || existingAttachment?.name}
                    <span style={{ marginLeft: Spacing.sm, color: ThemeColors.text.secondary }}>
                      ({attachment ? formatFileSize(attachment.size) : 'Existing file'})
                    </span>
                  </FileName>
                  <RemoveFileButton type="button" onClick={removeAttachment}>
                    <X />
                  </RemoveFileButton>
                </FileItem>
              ) : (
                <FileUploadArea
                  isDragging={isDragging}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadIcon>
                    <Upload />
                  </UploadIcon>
                  <UploadText>Click to upload or drag and drop</UploadText>
                  <UploadSubtext>PDF, DOC, DOCX, images (max 10MB)</UploadSubtext>
                </FileUploadArea>
              )}
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Users />
                Target Audience <Required>*</Required>
              </Label>
              <CheckboxGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={allUsers}
                    onChange={(e) => {
                      setAllUsers(e.target.checked);
                      if (e.target.checked) {
                        setSelectedRoles([]);
                        setSelectedUserIds([]);
                        setSelectedSalesGroups([]);
                        setSelectedProvinces([]);
                      }
                    }}
                  />
                  <span>All Users</span>
                </CheckboxLabel>
              </CheckboxGroup>

              {!allUsers && (
                <>
                  <CheckboxGroup style={{ marginTop: Spacing.md }}>
                    <div style={{ fontWeight: Typography.fontWeight.semiBold, marginBottom: Spacing.xs }}>
                      Roles
                    </div>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes('manager')}
                        onChange={() => toggleRole('manager')}
                      />
                      <span>Managers</span>
                    </CheckboxLabel>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes('sales-representative')}
                        onChange={() => toggleRole('sales-representative')}
                      />
                      <span>Sales Representatives</span>
                    </CheckboxLabel>
                  </CheckboxGroup>

                  <FormGroup>
                    <Label>Specific Users</Label>
                    <TagInput>
                      {selectedUserIds.map(userId => {
                        const user = availableUsers.find(u => u.id === userId);
                        return (
                          <TagChip key={userId}>
                            {user?.username || user?.email || userId}
                            <button type="button" onClick={() => removeUser(userId)}>
                              <X />
                            </button>
                          </TagChip>
                        );
                      })}
                      <TagInputField
                        type="text"
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        placeholder="Search users..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && filteredUsers.length > 0) {
                            e.preventDefault();
                            addUser(filteredUsers[0].id);
                          }
                        }}
                      />
                    </TagInput>
                    {userSearchTerm && filteredUsers.length > 0 && (
                      <div style={{
                        marginTop: Spacing.xs,
                        padding: Spacing.sm,
                        background: ThemeColors.background.secondary,
                        borderRadius: BorderRadius.md,
                        maxHeight: '150px',
                        overflowY: 'auto'
                      }}>
                        {filteredUsers.slice(0, 5).map(user => (
                          <div
                            key={user.id}
                            onClick={() => addUser(user.id)}
                            style={{
                              padding: Spacing.xs,
                              cursor: 'pointer',
                              borderRadius: BorderRadius.sm
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = ThemeColors.primary.light;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            {user.username || user.email}
                          </div>
                        ))}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label>Sales Groups</Label>
                    <TagInput>
                      {selectedSalesGroups.map(group => (
                        <TagChip key={group}>
                          {group}
                          <button type="button" onClick={() => removeSalesGroup(group)}>
                            <X />
                          </button>
                        </TagChip>
                      ))}
                      <TagInputField
                        type="text"
                        value={salesGroupInput}
                        onChange={(e) => setSalesGroupInput(e.target.value)}
                        placeholder="Enter sales group..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addSalesGroup();
                          }
                        }}
                      />
                    </TagInput>
                  </FormGroup>

                  <FormGroup>
                    <Label>Provinces/States</Label>
                    <TagInput>
                      {selectedProvinces.map(province => (
                        <TagChip key={province}>
                          {province}
                          <button type="button" onClick={() => removeProvince(province)}>
                            <X />
                          </button>
                        </TagChip>
                      ))}
                      <TagInputField
                        type="text"
                        value={provinceInput}
                        onChange={(e) => setProvinceInput(e.target.value)}
                        placeholder="Enter province/state..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addProvince();
                          }
                        }}
                      />
                    </TagInput>
                  </FormGroup>
                </>
              )}

              {errors.targetAudience && (
                <ErrorMessage>
                  <AlertCircle />
                  {errors.targetAudience}
                </ErrorMessage>
              )}
            </FormGroup>
          </FormContent>

          <FormActions>
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              <Save />
              {isLoading ? 'Saving...' : mode === 'create' ? 'Create Announcement' : 'Update Announcement'}
            </Button>
          </FormActions>
        </form>
      </FormContainer>
    </FormOverlay>
  );
};

